import { runInAction } from "mobx";
import { useToast } from "@chakra-ui/react";

import {
  AABB,
  getRandomHash,
  getRandomSeed,
  HitTest,
  IImageNode,
  IPathNode,
  IRectangleShapeNode,
  isUndefined,
  Lang,
  range,
  shallowCopy,
  SingleNode,
} from "@noli/core";
import { ExportOptions } from "@noli/svg";
import { BoardStore, translate, useSafeExecute } from "@noli/business";

import type {
  AIMetaType,
  DuplicateMeta,
  Img2ImgMeta,
  Img2ImgVariationMeta,
  InpaintingMeta,
  LandscapeSynthesisMeta,
  MattMeta,
  MetaType,
  PasteMeta,
  SuperResolutionMeta,
  Txt2ImgInpaintingMeta,
  Txt2ImgMeta,
  Txt2ImgOutpaintingMeta,
  Txt2ImgVariationMeta,
  UploadMeta,
} from "@/types/meta";
import type { _IImportThirdPartyMeta } from "@/thirdParty/meta";
import { tasksStore } from "@/stores/tasks";
import { statusStore } from "@/stores/status";
import {
  addNewImage,
  checkAIMeta,
  checkMeta,
  NewImageInfo,
  rectangleToImage,
  toast,
  toFixed,
} from "./misc";
import { postDrawRecord, SafeOp, SetupPostCreatorProps, VirtualNodeInfo } from "./safeOp";
import { injectPrompt } from "./prompt";
import {
  AI_MAX_WH,
  importantColorString2Label,
  INPAINTING_ALGORITHM_MAX_WH,
  INPAINTING_MAX_WH,
  LANDSCAPE_MAX_WH,
  SUPER_RESOLUTION_MAX_WH,
} from "./constants";
import { Editor_Words } from "./lang/editor";
import { APIs_Img2Img, APIs_Txt2Img } from "@/modules/editor/request/api";
import { injectSDDefaultsTo } from "@/modules/editor/utils/meta";
import { parametersStore } from "@/stores/parameters";
import { FreeTaskMessageElement } from "@/modules/editor/components/FreeTaskMessage";
import { THIRD_PARTY_MODEL_TYPES } from "@/thirdParty/constants";
import { Third_Party_Words } from "@/thirdParty/lang";
import { GenerateBizType } from "@/types/creator";
import { isAuthUser, isVip } from "./status";

function checkMaxWH(
  t: ReturnType<typeof useToast>,
  lang: Lang,
  w: number,
  h: number,
  maxWH: number,
): boolean {
  if (w * h > maxWH ** 2) {
    toast(
      t,
      "warning",
      translate(Editor_Words["max-wh-warning-message"], lang)
        .replace("$1", ` (${toFixed(w, 2)}, ${toFixed(h, 2)}) `)
        .replace("$2", ` (${maxWH}, ${maxWH}) `),
    );
    return false;
  }
  return true;
}

type BatchSupportParams<T> = T & { generateCount?: number };
type WithBizType<T> = T & { bizType?: GenerateBizType };
// post creator

type ITxt2Img = BatchSupportParams<
  WithBizType<{
    t: ReturnType<typeof useToast>;
    lang: Lang;
    meta: Txt2ImgMeta | Txt2ImgVariationMeta;
    busyAliases: string[];
    aliasPrefix: string;
    displayNode?: IRectangleShapeNode;
    virtualNodeInfo?: VirtualNodeInfo[];
    freezePrompt?: boolean;
    noToast?: boolean;
    type: "txt2img" | "txt2img.variation";
  }>
>;
async function txt2img({
  t,
  lang,
  meta,
  busyAliases,
  aliasPrefix,
  displayNode,
  virtualNodeInfo,
  freezePrompt,
  noToast,
  generateCount = 1,
  type,
  bizType,
}: ITxt2Img): Promise<void> {
  // check maxWH
  if (!checkMaxWH(t, lang, meta.data.w, meta.data.h, AI_MAX_WH)) {
    if (virtualNodeInfo) {
      await SafeOp.cleanupSetupBatch(busyAliases, virtualNodeInfo);
    }
    return;
  }
  const uri = APIs_Txt2Img.sd;

  if (!virtualNodeInfo && displayNode?.type === "rectangle" && generateCount === 1) {
    const setupPostCreatorProps: SetupPostCreatorProps = [
      t,
      uri,
      lang,
      busyAliases,
      [displayNode],
      noToast,
    ];
    virtualNodeInfo = await SafeOp.setupPostCreator(...setupPostCreatorProps);
  } else if (!virtualNodeInfo) {
    // setup
    await SafeOp.setupBatchPostCreator(t, lang, busyAliases, noToast);
  }
  // inject prompt
  const success = await injectPrompt(t, meta, freezePrompt);
  if (!success) {
    await SafeOp.cleanupSetupBatch(busyAliases, virtualNodeInfo);
    return;
  }
  // post to creator
  injectSDDefaultsTo(uri, meta);
  const bizTypeMap = {
    txt2img: GenerateBizType.Txt2Img,
    "txt2img.variation": GenerateBizType.Variation,
  };
  await SafeOp.postCreator({
    t,
    uri,
    meta,
    data: { use_seed: true, ...meta.data },
    lang,
    busyAliases,
    virtualNodeInfo,
    postprocess: async ({ cdn, bbox, callback, response }) => {
      const url = cdn;
      const callbacks = {
        success: callback,
        failed: async () =>
          toast(t, "error", translate(Editor_Words["add-image-error-message"], lang)),
      };

      const newAlias = `${aliasPrefix}.${getRandomHash()}`;
      console.log("text 2 img newAlias: ", newAlias);
      addNewImage(newAlias, url, {
        info: bbox,
        meta: {
          type: meta.type,
          data: { ...meta.data, ...((response?.meta ?? meta.data) as any) },
        },
        callbacks,
      });
      // }
    },
    generateCount,
    bizType: bizType ?? bizTypeMap[type],
  });
}

type IImg2Img = BatchSupportParams<
  WithBizType<{
    t: ReturnType<typeof useToast>;
    lang: Lang;
    meta: Img2ImgMeta | Img2ImgVariationMeta;
    busyAliases: string[];
    aliasPrefix: string;
    displayNode?: IImageNode | IPathNode;
    virtualNodeInfo?: VirtualNodeInfo[];
    freezePrompt?: boolean;
    noToast?: boolean;
    type: "img2img" | "img2img.variation";
    bizType?: GenerateBizType;
  }>
>;
async function img2img({
  t,
  lang,
  meta,
  busyAliases,
  aliasPrefix,
  displayNode,
  virtualNodeInfo,
  freezePrompt,
  noToast,
  generateCount = 1,
  type,
  bizType,
}: IImg2Img): Promise<void> {
  async function urlCallback(url: string, virtualNodeInfo?: VirtualNodeInfo[]): Promise<void> {
    // check maxWH
    if (!checkMaxWH(t, lang, meta.data.w, meta.data.h, AI_MAX_WH)) {
      return failedFn(virtualNodeInfo)();
    }
    // inject prompt
    const success = await injectPrompt(t, meta, freezePrompt);
    if (!success) {
      return failedFn(virtualNodeInfo)(true);
    }
    // add new image
    meta.data.url = url;
    injectSDDefaultsTo(uri, meta);
    const bizTypeMap = {
      img2img: GenerateBizType.Img2Img,
      "img2img.variation": GenerateBizType.Variation,
    };
    await SafeOp.postCreator({
      t,
      uri,
      meta,
      data: { use_seed: true, keep_alpha, ...meta.data },
      lang,
      busyAliases,
      virtualNodeInfo,
      postprocess: SafeOp.addNewImageGenerator(aliasPrefix, meta),
      generateCount,
      bizType: bizType ?? bizTypeMap[type],
    });
  }

  // setup
  const uri = APIs_Img2Img.sd;
  const setup = async () => {
    if (virtualNodeInfo) return virtualNodeInfo;
    await SafeOp.setupBatchPostCreator(t, lang, busyAliases, noToast);
  };
  const failedFn = (virtualNodeInfo?: VirtualNodeInfo[]) => async (noToast?: boolean) => {
    await SafeOp.cleanupSetupBatch(busyAliases, virtualNodeInfo);
    !noToast && toast(t, "error", translate(Editor_Words["post-creator-error-message"], lang));
  };
  // get url
  let keep_alpha = false;
  if (meta.data.url) return urlCallback(meta.data.url, await setup());
  if (!displayNode) {
    toast(t, "error", "either `url` or `displayNode` should be provided 😫");
    return;
  }
  if (displayNode.type === "image") {
    const srcInfo = await SafeOp.getSuitableSrcInfo(
      t,
      lang,
      displayNode,
      AI_MAX_WH,
      setup,
      failedFn,
    );
    if (!srcInfo) return;
    keep_alpha = true;
    meta.data.w = srcInfo.wh.w;
    meta.data.h = srcInfo.wh.h;
    meta.data.from = shallowCopy(displayNode.meta) as MetaType | undefined;
    return urlCallback(srcInfo.src, srcInfo.virtualNodeInfo);
  }
  const tmpNode = displayNode.snapshot();
  const bounding = tmpNode.bbox.bounding.toAABB();
  const { w, h } = bounding;
  if (w > 512) {
    meta.data.w = w;
  }
  if (h > 512) {
    meta.data.h = h;
  }
  virtualNodeInfo = await setup();
  const paddingX = 0.5 * Math.max(0, 512 - w);
  const paddingY = 0.5 * Math.max(0, 512 - h);
  const blob = await SafeOp.exportSingleNodeBlob(tmpNode, { t, lang, paddingX, paddingY });
  if (!blob) {
    return failedFn(virtualNodeInfo)();
  }
  const urls = await SafeOp.uploadImage(t, lang, blob, {
    failed: failedFn(virtualNodeInfo),
    returnWH: false,
  });
  if (!urls) return;
  await urlCallback(urls.cdn, virtualNodeInfo);
}

type ITxt2ImgInpainting = BatchSupportParams<
  WithBizType<{
    t: ReturnType<typeof useToast>;
    lang: Lang;
    meta: Txt2ImgInpaintingMeta;
    virtualNodeInfo?: VirtualNodeInfo[];
    busyAliases: string[];
    freezePrompt?: boolean;
    noToast?: boolean;
  }>
>;
async function txt2imgInpainting({
  t,
  lang,
  meta,
  virtualNodeInfo,
  busyAliases,
  freezePrompt,
  noToast,
  generateCount = 1,
  bizType,
}: ITxt2ImgInpainting): Promise<void> {
  // check maxWH
  if (!checkMaxWH(t, lang, meta.data.w, meta.data.h, AI_MAX_WH)) {
    if (virtualNodeInfo) {
      await SafeOp.cleanupSetupBatch(busyAliases, virtualNodeInfo);
    }
    return;
  }
  // setup
  const uri = APIs_Txt2Img["sd.inpainting"];
  await SafeOp.setupBatchPostCreator(t, lang, busyAliases, noToast);

  // inject prompt
  const success = await injectPrompt(t, meta, freezePrompt);
  if (!success) {
    await SafeOp.cleanupSetupBatch(busyAliases, virtualNodeInfo);
    return;
  }
  // post to creator
  injectSDDefaultsTo(uri, meta);
  await SafeOp.postCreator({
    t,
    uri,
    meta,
    data: { use_seed: true, ...meta.data },
    lang,
    busyAliases,
    virtualNodeInfo,
    postprocess: async ({ cdn, bbox, callback, response }) => {
      const callbacks = {
        success: callback,
        failed: async () =>
          toast(t, "error", translate(Editor_Words["add-image-error-message"], lang)),
      };
      const newAlias = `txt2img.inpainting.${getRandomHash()}`;
      addNewImage(newAlias, cdn, {
        info: bbox,
        meta: {
          type: meta.type,
          data: { ...meta.data, ...((response?.meta ?? meta.data) as any) },
        },
        callbacks,
      });
    },
    generateCount,
    bizType: bizType ?? GenerateBizType.Replace,
  });
}

type IOutpainting = WithBizType<{
  t: ReturnType<typeof useToast>;
  lang: Lang;
  meta: Txt2ImgOutpaintingMeta;
  virtualNodeInfo?: VirtualNodeInfo[];
  busyAliases: string[];
  noToast?: boolean;
}>;
async function outpainting({
  t,
  lang,
  meta,
  virtualNodeInfo,
  busyAliases,
  noToast,
  bizType,
}: IOutpainting): Promise<void> {
  console.log("outpainting bizType: ", bizType);

  // check maxWH
  if (!checkMaxWH(t, lang, meta.data.w, meta.data.h, AI_MAX_WH)) {
    if (virtualNodeInfo) {
      await SafeOp.cleanupSetupBatch(busyAliases, virtualNodeInfo);
    }
    return;
  }
  // setup
  const uri = APIs_Txt2Img["sd.outpainting"];
  if (!virtualNodeInfo) {
    const virtualInfo = [{ w: meta.data.w, h: meta.data.h }];
    const setupPostCreatorProps: SetupPostCreatorProps = [
      t,
      uri,
      lang,
      busyAliases,
      virtualInfo,
      noToast,
    ];
    virtualNodeInfo = await SafeOp.setupPostCreator(...setupPostCreatorProps);
  }
  // add new image
  await SafeOp.postCreator({
    t,
    uri,
    meta,
    data: { use_seed: true, ...meta.data },
    lang,
    busyAliases,
    virtualNodeInfo,
    postprocess: async ({ cdn, bbox, callback, virtualAlias }) => {
      const areaNode = BoardStore.graph.getNode<IRectangleShapeNode>(virtualAlias!);
      if (areaNode) {
        const callbacks = {
          success: callback,
          failed: async (error: any) => {
            console.error("error: ", error);
            toast(t, "error", translate(Editor_Words["update-op-error-message"], lang));
          },
        };
        const imageNode = rectangleToImage(areaNode, cdn);
        imageNode.meta = meta;
        imageNode.bbox = bbox;
        const json = imageNode.toJson();
        useSafeExecute(
          "update",
          null,
          true,
          callbacks,
        )({ alias: virtualAlias!, json, noSelect: true });
      } else {
        const callbacks = {
          success: callback,
          failed: async () =>
            toast(t, "error", translate(Editor_Words["add-image-error-message"], lang)),
        };
        const newAlias = `outpainting.${getRandomHash()}`;
        addNewImage(newAlias, cdn, { info: bbox, meta, callbacks });
      }
      runInAction(() => {
        statusStore.outpaintingAreaAlias = undefined;
        statusStore.outpaintingSrc = undefined;
      });
    },
    generateCount: 1,
    bizType: bizType ?? GenerateBizType.Outpainting,
  });
}

type ISuperResolution = WithBizType<{
  t: ReturnType<typeof useToast>;
  lang: Lang;
  meta: SuperResolutionMeta;
  virtualNodeInfo?: VirtualNodeInfo[];
  busyAliases: string[];
  noToast?: boolean;
}>;
async function superResolution({
  t,
  lang,
  meta,
  virtualNodeInfo,
  busyAliases,
  noToast,
  bizType,
}: ISuperResolution): Promise<void> {
  // check maxWH
  if (!checkMaxWH(t, lang, meta.data.wh.w, meta.data.wh.h, SUPER_RESOLUTION_MAX_WH)) {
    if (virtualNodeInfo) {
      await SafeOp.cleanupSetup(busyAliases, virtualNodeInfo);
    }
    return;
  }
  // setup
  const uri = APIs_Img2Img.sr;
  if (!virtualNodeInfo) {
    const virtualInfo = [meta.data.wh];
    const setupPostCreatorProps: SetupPostCreatorProps = [
      t,
      uri,
      lang,
      busyAliases,
      virtualInfo,
      noToast,
    ];
    virtualNodeInfo = await SafeOp.setupPostCreator(...setupPostCreatorProps);
  }

  await SafeOp.postCreator({
    t,
    uri,
    meta,
    data: meta.data,
    lang,
    busyAliases,
    virtualNodeInfo,
    // callback is add new image
    postprocess: SafeOp.addNewImageGenerator("sr", meta),
    generateCount: 1,
    bizType: bizType ?? GenerateBizType.SuperRsolution,
  });
  // await SafeOp.addNewImageWith(t, uri, meta.data, lang, busyAliases, "sr", {
  //   virtualNodeInfo,
  //   meta,
  // });
}

type IInpainting = WithBizType<{
  t: ReturnType<typeof useToast>;
  lang: Lang;
  meta: InpaintingMeta;
  virtualNodeInfo?: VirtualNodeInfo[];
  busyAliases: string[];
  noToast?: boolean;
}>;
async function inpainting({
  t,
  lang,
  meta,
  virtualNodeInfo,
  busyAliases,
  noToast,
  bizType,
}: IInpainting): Promise<void> {
  // check maxWH
  if (!checkMaxWH(t, lang, meta.data.wh.w, meta.data.wh.h, INPAINTING_MAX_WH)) {
    if (virtualNodeInfo) {
      await SafeOp.cleanupSetup(busyAliases, virtualNodeInfo);
    }
    return;
  }
  // setup
  const uri = APIs_Img2Img.inpainting;
  if (!virtualNodeInfo) {
    const virtualInfo = [meta.data.wh];
    const setupPostCreatorProps: SetupPostCreatorProps = [
      t,
      uri,
      lang,
      busyAliases,
      virtualInfo,
      noToast,
    ];
    virtualNodeInfo = await SafeOp.setupPostCreator(...setupPostCreatorProps);
  }

  await SafeOp.postCreator({
    t,
    uri,
    meta,
    data: { max_wh: INPAINTING_ALGORITHM_MAX_WH, ...meta.data },
    lang,
    busyAliases,
    virtualNodeInfo,
    // callback is add new image
    postprocess: SafeOp.addNewImageGenerator("inpainting", meta),
    generateCount: 1,
    bizType: bizType ?? GenerateBizType.Erase,
  });
}

type ILandscapeSynthesis = {
  t: ReturnType<typeof useToast>;
  lang: Lang;
  meta: LandscapeSynthesisMeta;
  virtualNodeInfo?: VirtualNodeInfo[];
  busyAliases: string[];
  noToast?: boolean;
};
async function landscapeSynthesis({
  t,
  lang,
  meta,
  virtualNodeInfo,
  busyAliases,
  noToast,
}: ILandscapeSynthesis): Promise<void> {
  // check maxWH
  if (!checkMaxWH(t, lang, meta.data.wh.w, meta.data.wh.h, LANDSCAPE_MAX_WH)) {
    if (virtualNodeInfo) {
      await SafeOp.cleanupSetup(busyAliases, virtualNodeInfo);
    }
    return;
  }
  // setup
  const uri = APIs_Img2Img.semantic2img;
  if (!virtualNodeInfo) {
    const virtualInfo = [meta.data.wh];
    const setupPostCreatorProps: SetupPostCreatorProps = [
      t,
      uri,
      lang,
      busyAliases,
      virtualInfo,
      noToast,
    ];

    virtualNodeInfo = await SafeOp.setupPostCreator(...setupPostCreatorProps);
  }
  await SafeOp.postCreator({
    t,
    uri,
    meta,
    data: {
      color2label: importantColorString2Label,
      ...meta.data,
    },
    lang,
    busyAliases,
    virtualNodeInfo,
    // callback is add new image
    postprocess: SafeOp.addNewImageGenerator("landscape-synthesis", meta),
    generateCount: 1,
    bizType: GenerateBizType.Landscape,
  });
}

type SetupInfo = {
  // in most cases busy-checking should be done outside, then `checkBusy` should be set to false
  checkBusy: boolean;
  busyAliases: string[];
  virtualNodeInfo?: VirtualNodeInfo[];
};
type _IImportTxt2ImgMeta = {
  type: (Txt2ImgMeta | Txt2ImgVariationMeta)["type"];
  meta: Txt2ImgMeta | Txt2ImgVariationMeta;
  opt: {
    freezePrompt: boolean;
    displayNode?: IRectangleShapeNode;
    generateCount?: number;
  };
};
type _IImportImg2ImgMeta = {
  type: (Img2ImgMeta | Img2ImgVariationMeta)["type"];
  meta: Img2ImgMeta | Img2ImgVariationMeta;
  opt: {
    freezePrompt: boolean;
    displayNode?: IImageNode | IPathNode;
    generateCount?: number;
  };
};
type _IImportTxt2ImgInpaintingMeta = {
  type: Txt2ImgInpaintingMeta["type"];
  meta: Txt2ImgInpaintingMeta;
  opt: { freezePrompt: boolean; generateCount?: number };
};
type _IImportTxt2ImgOutpaintingMeta = {
  type: Txt2ImgOutpaintingMeta["type"];
  meta: Txt2ImgOutpaintingMeta;
  opt: null;
};
type _IImportUploadMeta = {
  type: UploadMeta["type"];
  meta: UploadMeta;
  opt: null;
};
type _IImportPasteMeta = {
  type: PasteMeta["type"];
  meta: PasteMeta;
  opt: null;
};
type _IImportSuperResolutionMeta = {
  type: SuperResolutionMeta["type"];
  meta: SuperResolutionMeta;
  opt: null;
};
type _IImportInpaintingMeta = {
  type: InpaintingMeta["type"];
  meta: InpaintingMeta;
  opt: null;
};
type _IImportLandscapeSynthesisMeta = {
  type: LandscapeSynthesisMeta["type"];
  meta: LandscapeSynthesisMeta;
  opt: null;
};
type _IImportDuplicateMeta = {
  type: DuplicateMeta["type"];
  meta: DuplicateMeta;
  opt: null;
};
type _IImportMattMeta = {
  type: MattMeta["type"];
  meta: MattMeta;
  opt: null;
};
type IImportMeta = WithBizType<
  {
    t: ReturnType<typeof useToast>;
    lang: Lang;
    setupInfo: SetupInfo;
    noFreeze?: boolean;
    noToast?: boolean;
    aliasPrefix?: string;
    bizType?: GenerateBizType;
  } & (
    | _IImportTxt2ImgMeta
    | _IImportImg2ImgMeta
    | _IImportTxt2ImgInpaintingMeta
    | _IImportTxt2ImgOutpaintingMeta
    | _IImportUploadMeta
    | _IImportPasteMeta
    | _IImportSuperResolutionMeta
    | _IImportInpaintingMeta
    | _IImportLandscapeSynthesisMeta
    | _IImportThirdPartyMeta
    | _IImportDuplicateMeta
    | _IImportMattMeta
  )
>;
type IImportMetaInternal = IImportMeta & {
  aliasPrefix: string;
};
async function importMetaInternal({
  t,
  lang,
  type,
  meta,
  setupInfo: { checkBusy, busyAliases, virtualNodeInfo },
  aliasPrefix,
  noToast,
  opt,
  bizType,
}: IImportMetaInternal): Promise<void> {
  if (checkBusy && busyAliases.some((busyAlias) => tasksStore.busyNodes.has(busyAlias))) {
    toast(t, "info", translate(Editor_Words["already-in-queue-message"], lang));
    return;
  }
  meta.data.timestamp = Date.now();

  if (checkAIMeta(meta)) {
    meta.data.localModels = parametersStore.activatedLocalModels;
  }
  if (THIRD_PARTY_MODEL_TYPES.includes(type as any)) {
    toast(t, "warning", translate(Third_Party_Words["no-import-warning"], lang));
    return;
  }
  switch (type) {
    // common
    case "upload": {
      const success = async () => {
        toast(t, "success", translate(Editor_Words["upload-seccess-message"], lang));
        if (isAuthUser()) {
          postDrawRecord(meta.data.url, Date.now() / 1000 - createTime, meta);
        }
      };
      const failed = async () => {
        toast(t, "error", translate(Editor_Words["upload-image-error-message"], lang));
      };
      const createTime = Date.now() / 1000;
      const getAlias = (meta: UploadMeta | PasteMeta) => {
        if ((meta as UploadMeta).data.isDrag) {
          return "drag-";
        }
        if ((meta as PasteMeta).data.isPaste) {
          return "paste-";
        }
        return "";
      };
      const newAlias = `${getAlias(meta)}upload.${getRandomHash()}`;
      const bboxInfo: NewImageInfo = meta.data.wh;
      addNewImage(newAlias, meta.data.url, {
        info: bboxInfo,
        meta,
        callbacks: { success, failed },
        noSelect: false,
      });

      break;
    }
    case "duplicate": {
      const createTime = Date.now() / 1000;
      const { duplicateFrom, wh } = meta.data;
      const newAlias = `duplicate${duplicateFrom ? `-${duplicateFrom}` : ""}.${getRandomHash()}`;
      const success = async () => {
        toast(t, "success", translate(Editor_Words["add-to-board-success-message"], lang));
        if (isAuthUser()) {
          postDrawRecord(meta.data.url, Date.now() / 1000 - createTime, meta);
        }
        BoardStore.api.select([newAlias]);
      };
      const failed = async () => {
        toast(t, "error", translate(Editor_Words["add-to-board-error-message"], lang));
      };
      addNewImage(newAlias, meta.data.url, {
        info: wh,
        meta,
        callbacks: { success, failed },
      });

      break;
    }
    case "SOD": {
      const createTime = Date.now() / 1000;
      const { wh } = meta.data;
      const newAlias = `SOD.${getRandomHash()}`;
      const success = async () => {
        toast(t, "success", translate(Editor_Words["add-to-board-success-message"], lang));
        if (isAuthUser()) {
          postDrawRecord(meta.data.url, Date.now() / 1000 - createTime, meta);
        }
        BoardStore.api.select([newAlias]);
      };
      const failed = async () => {
        toast(t, "error", translate(Editor_Words["add-to-board-error-message"], lang));
      };
      addNewImage(newAlias, meta.data.url, {
        info: wh,
        meta,
        callbacks: { success, failed },
      });

      break;
    }
    case "sr": {
      return superResolution({
        t,
        lang,
        meta,
        virtualNodeInfo,
        busyAliases,
        noToast,
        bizType,
      });
    }
    case "inpainting": {
      return inpainting({
        t,
        lang,
        meta,
        virtualNodeInfo,
        busyAliases,
        noToast,
        bizType,
      });
    }
    case "landscape": {
      return landscapeSynthesis({
        t,
        lang,
        meta,
        virtualNodeInfo,
        busyAliases,
        noToast,
      });
    }
    case "txt2img":
    case "txt2img.variation": {
      return txt2img({
        t,
        lang,
        meta,
        busyAliases,
        aliasPrefix,
        virtualNodeInfo,
        noToast,
        ...opt,
        generateCount: opt.generateCount ?? 1,
        type,
        bizType,
      });
    }
    case "img2img":
    case "img2img.variation": {
      return img2img({
        t,
        lang,
        meta,
        busyAliases,
        aliasPrefix,
        virtualNodeInfo,
        noToast,
        type,
        ...opt,
        generateCount: opt.generateCount ?? 1,
        bizType,
      });
    }
    case "txt2img.inpainting": {
      return txt2imgInpainting({
        t,
        lang,
        meta,
        virtualNodeInfo,
        busyAliases,
        freezePrompt: opt.freezePrompt,
        noToast,
        generateCount: opt.generateCount,
        bizType,
      });
    }
    case "txt2img.outpainting": {
      return outpainting({
        t,
        lang,
        meta,
        virtualNodeInfo,
        busyAliases,
        noToast,
        bizType,
      });
    }
    default: {
      toast(
        t,
        "warning",
        `${translate(Editor_Words["unrecognized-meta-type-warning-message"], lang)}${type}`,
      );
    }
  }
}

type ISDVariation = BatchSupportParams<
  WithBizType<{
    t: ReturnType<typeof useToast>;
    lang: Lang;
    meta: AIMetaType;
    busyAliases: string[];
    fidelity: number;
  }>
>;

type InpaintingBGFGOptions =
  | {
      type: InpaintingMeta["type"];
      useRefine?: boolean;
      refineFidelity?: number;
      useFGAsImage: boolean;
      noMask?: boolean;
      usePipeline?: boolean;
    }
  | {
      type: Txt2ImgInpaintingMeta["type"];
      useFGAsImage: boolean;
      noMask?: boolean;
      txt2imgMetaData: Txt2ImgMeta["data"];
      generateCount?: number;
    };

interface OutpaintingOptions {
  text: string;
  negativePrompt: string;
}

// unified actions

export class Actions {
  // if `virtualNodeInfo` is provided, we should check `busyNode` outside
  // if action support BATCH generate, we should NOT create `virtualNodeInfo` outside
  static async importMeta(opt: IImportMeta): Promise<void> {
    opt.aliasPrefix ??= `import.${opt.meta.type}`;
    await importMetaInternal(opt as IImportMetaInternal);
  }

  static async sdVariation({
    t,
    lang,
    meta,
    busyAliases,
    fidelity,
    generateCount = 1,
    bizType,
  }: ISDVariation): Promise<void> {
    const variationStrength = 1.0 - fidelity;
    // TODO
    if (meta.type === "txt2img.outpainting") return;
    meta = shallowCopy(meta);
    const seed = getRandomSeed();
    const current =
      meta.type === "txt2img.variation" || meta.type === "img2img.variation"
        ? meta.data.variations
        : [];
    const variations = current.concat([{ seed, strength: variationStrength }]);
    const aliasPrefix =
      meta.type === "txt2img" || meta.type == "txt2img.variation"
        ? "txt2img.variation"
        : "img2img.variation";
    if (meta.type === "txt2img") {
      (meta as AIMetaType).type = "txt2img.variation";
    } else if (meta.type === "img2img") {
      (meta as AIMetaType).type = "img2img.variation";
    }
    (meta as Txt2ImgVariationMeta | Img2ImgVariationMeta).data.variations = variations;
    await Actions.importMeta({
      t,
      lang,
      type: meta.type,
      meta,
      setupInfo: { checkBusy: false, busyAliases },
      aliasPrefix,
      opt: { freezePrompt: true, generateCount },
      bizType,
    } as any);
  }

  static async inpaintingWithBGFG(
    data: WithBizType<{
      t: ReturnType<typeof useToast>;
      lang: Lang;
      bgAlias: string | undefined;
      fgAlias: string | undefined;
      opt: InpaintingBGFGOptions;
      noToast?: boolean;
      checkBusy?: boolean;
    }>,
  ): Promise<void> {
    const { t, lang, bgAlias, fgAlias, opt, noToast, checkBusy, bizType } = data;
    // sanity check
    if (isUndefined(bgAlias) || isUndefined(fgAlias)) {
      return;
    }
    if (checkBusy && (tasksStore.busyNodes.has(bgAlias) || tasksStore.busyNodes.has(fgAlias))) {
      if (!isVip()) {
        if (!isAuthUser()) {
          toast(t, "info", translate(Editor_Words["already-in-queue-message"], lang));
          return;
        } else {
          toast(t, "info", FreeTaskMessageElement);
          return;
        }
      }
    }
    const bgNode = BoardStore.graph.getExistingNode<SingleNode>(bgAlias);
    const fgNode = BoardStore.graph.getExistingNode<SingleNode>(fgAlias);
    const bgBBox = bgNode.bbox;
    const fgBBox = fgNode.bbox;
    if (!HitTest.test(bgBBox, fgBBox)) {
      toast(t, "warning", translate(Editor_Words["no-overlap-error-message"], lang));
      return;
    }
    // apply inpainting
    const imageNodes = opt.useFGAsImage ? [bgNode, fgNode] : [bgNode];
    const maskNodes = opt.noMask ?? false ? [] : [fgNode];
    // setup
    const uri = opt.type === "inpainting" ? APIs_Img2Img.inpainting : APIs_Txt2Img["sd.inpainting"];
    const busyAliases = Array.from(
      new Set(imageNodes.map((node) => node.alias).concat(maskNodes.map((node) => node.alias))),
    );
    const bounding = bgBBox.bounding.toAABB();
    const { w, h } = bounding.wh;
    /// check maxWH
    if (!checkMaxWH(t, lang, w, h, INPAINTING_MAX_WH)) {
      return;
    }

    const virtualInfo = { w: toFixed(w, 2), h: toFixed(h, 2) };
    const setupPostCreatorProps: SetupPostCreatorProps = [
      t,
      uri,
      lang,
      busyAliases,
      [virtualInfo],
      noToast,
    ];
    const virtualNodeInfo =
      opt.type === "inpainting"
        ? await SafeOp.setupPostCreator(...setupPostCreatorProps)
        : undefined;

    const failed = async () => {
      if (opt.type === "inpainting") {
        await SafeOp.cleanupSetup(busyAliases, virtualNodeInfo);
      }
      toast(t, "error", translate(Editor_Words["post-creator-error-message"], lang));
    };
    // export nodes
    const exportOptions: ExportOptions = { exportBox: bounding };
    const imageBlob = await SafeOp.exportBlob(imageNodes, { t, lang, exportOptions });
    if (!imageBlob) {
      return failed();
    }
    const imageURLs = await SafeOp.uploadImage(t, lang, imageBlob, { failed, returnWH: false });
    if (!imageURLs) return;
    const imageCDN = imageURLs.cdn;

    if (maskNodes.length === 0) return postCreator("");
    const maskBlob = await SafeOp.exportBlob(maskNodes, { t, lang, exportOptions });
    if (!maskBlob) {
      return failed();
    }
    const maskURLs = await SafeOp.uploadImage(t, lang, maskBlob, { failed, returnWH: false });
    if (!maskURLs) return;
    await postCreator(maskURLs.cdn);

    async function postCreator(maskCDN: string): Promise<void> {
      const type = opt.type;
      const commonData = {
        url: imageCDN,
        mask_url: maskCDN,
        from:
          imageNodes.length !== 1
            ? undefined
            : checkMeta(imageNodes[0].meta)
            ? imageNodes[0].meta
            : undefined,
      };
      if (type === "inpainting") {
        await Actions.importMeta({
          t,
          lang,
          type,
          meta: {
            type,
            data: {
              ...commonData,
              wh: virtualInfo,
              // use_refine: opt.useRefine,
              // refine_fidelity: opt.refineFidelity ?? 0.8,
              use_pipeline: opt.usePipeline ?? false,
            },
          },
          setupInfo: { checkBusy: false, busyAliases, virtualNodeInfo },
          opt: null,
        });
      } else {
        await Actions.importMeta({
          t,
          lang,
          type,
          meta: {
            type,
            data: {
              ...commonData,
              ...opt.txt2imgMetaData,
              w: Math.round(w),
              h: Math.round(h),
            },
          },
          setupInfo: { checkBusy: false, busyAliases },
          opt: { freezePrompt: false, generateCount: opt.generateCount },
          bizType,
        });
      }
    }
  }

  static async outpaintingWith(
    data: WithBizType<{
      t: ReturnType<typeof useToast>;
      lang: Lang;
      bounding: AABB;
      areaNode: IRectangleShapeNode;
      overlappedNodes: SingleNode[];
      opt: OutpaintingOptions;
      noToast?: boolean;
      checkBusy?: boolean;
    }>,
  ): Promise<void> {
    const { t, lang, bounding, areaNode, overlappedNodes, opt, noToast, checkBusy, bizType } = data;
    console.log("outpaintingWith bizType: ", bizType);
    // check maxWH
    const { w, h } = bounding.wh;
    if (!checkMaxWH(t, lang, w, h, AI_MAX_WH)) {
      return;
    }
    const areaAlias = areaNode.alias;
    // setup
    const uri = APIs_Txt2Img["sd.outpainting"];
    const busyAliases = checkBusy
      ? Array.from(new Set(overlappedNodes.map((node) => node.alias).concat([areaAlias])))
      : [];
    if (checkBusy && busyAliases.some((busyAlias) => tasksStore.busyNodes.has(busyAlias))) {
      if (!isVip()) {
        if (!isAuthUser()) {
          toast(t, "info", translate(Editor_Words["already-in-queue-message"], lang));
          return;
        } else {
          toast(t, "info", FreeTaskMessageElement);
          return;
        }
      }
    }
    const setupPostCreatorProps: SetupPostCreatorProps = [
      t,
      uri,
      lang,
      busyAliases,
      [areaNode],
      noToast,
    ];
    const virtualNodeInfo = await SafeOp.setupPostCreator(...setupPostCreatorProps);
    const failed = async () => {
      await SafeOp.cleanupSetup(busyAliases, virtualNodeInfo);
      toast(t, "error", translate(Editor_Words["post-creator-error-message"], lang));
    };
    // inject prompt
    const seed = getRandomSeed();
    const meta: AIMetaType = {
      type: "txt2img.outpainting",
      data: {
        w: toFixed(w, 2),
        h: toFixed(Math.abs(h), 2),
        text: "",
        seed,
        use_circular: false,
        url: "",
        promptInfo: {
          lang,
          original: opt.text,
          originalNegative: opt.negativePrompt,
        },
      },
    };
    const success = await injectPrompt(t, meta);
    if (!success) {
      return failed();
    }
    // export nodes
    const exportOptions: ExportOptions = { exportBox: bounding };
    const overlappedBlob = await SafeOp.exportBlob(overlappedNodes, { t, lang, exportOptions });
    if (!overlappedBlob) {
      return failed();
    }
    const urls = await SafeOp.uploadImage(t, lang, overlappedBlob, { failed, returnWH: false });
    if (!urls) return;
    // execute
    meta.data.url = urls.cdn;
    await Actions.importMeta({
      t,
      lang,
      type: "txt2img.outpainting",
      meta,
      setupInfo: {
        checkBusy: false,
        busyAliases,
        virtualNodeInfo,
      },
      opt: null,
      bizType,
    });
  }
}
